import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { FaroText, grey, NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import type { JSX } from "react";
import { PointCloudStatsView } from "./point-cloud-stats-view";
import { PointLoadingView } from "./point-loading-view";
import { PointOpacityView } from "./point-opacity-view";
import { PostProcessingView } from "./post-processing-view";
import { RenderedNodesView } from "./rendered-nodes-view";
import { useRenderingStatsContext } from "./rendering-stats-context";

/**
 * @param props to pass to the overlay.
 * @returns A developer-only overlay showing debugging information related to rendering.
 * This is only shown if the feature flag `RenderingDebugOverlay` is enabled.
 */
export function RenderingDebugOverlay(): JSX.Element | null {
  const isEnabled = useAppSelector(selectHasFeature(Features.RenderingDebug));

  if (!isEnabled) {
    return null;
  }

  return <InnerRenderingDebugOverlay />;
}

/**
 * @returns Inner component implementing the actual overlay.
 *  Separated so the hooks are only executed when the overlay is actually rendered.
 */
function InnerRenderingDebugOverlay(): JSX.Element {
  const { pointCloudStatsMap } = useRenderingStatsContext();

  return (
    <Stack
      className={NO_TRANSLATE_CLASS}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        backgroundColor: `${grey[950]}bb`,
        m: 2,
        borderRadius: 1,
        width: "fit-content",
      }}
    >
      <FaroText
        dark
        variant="heading12"
        sx={{ textTransform: "uppercase", p: 2, pb: 1 }}
      >
        Rendering Debug Overlay (DEV)
      </FaroText>

      <PointCloudStatsView pointCloudStatsMap={pointCloudStatsMap} />

      <RenderedNodesView />

      <PointLoadingView />

      <PointOpacityView />

      <PostProcessingView />
    </Stack>
  );
}
